import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { ObservationRecord, ObservationSourceCategory } from '../types/observations';
import type { LabResultRecord } from '../types/labs';
import { filterVisibleToMember } from '../utils/labResultVisibility';

export function sortObservationsNewestFirst<T extends { observedAt?: string | null; updatedAt?: unknown; createdAt?: unknown }>(items: T[]) {
  return [...items].sort((left, right) => {
    const leftTime = new Date(left.observedAt || (left.updatedAt as string) || (left.createdAt as string) || 0).getTime();
    const rightTime = new Date(right.observedAt || (right.updatedAt as string) || (right.createdAt as string) || 0).getTime();
    return rightTime - leftTime;
  });
}

function mapLegacyWearableObservation(userId: string, documentId: string, data: Record<string, any>): ObservationRecord {
  const code = data.code?.coding?.[0] || {};
  return {
    id: `legacy-${documentId}`,
    userId,
    sourceCategory: 'wearable',
    sourceDocumentId: documentId,
    sourceVendor: data.sourceVendor || null,
    sourceType: data.sourceType || 'wearable_observation',
    sourceSystem: data.sourceSystem || 'wearable_ingestion',
    codeSystem: code.system || data.codeSystem || null,
    code: code.code || data.code || null,
    display: code.display || data.display || 'Observation',
    valueNumber: typeof data.valueQuantity?.value === 'number' ? data.valueQuantity.value : data.valueNumber ?? null,
    valueString: data.valueString || data.value || null,
    unit: data.valueQuantity?.unit || data.unit || null,
    observedAt: data.effectiveDateTime || data.observedAt || null,
    reviewState: data.reviewState || 'machine_ingested',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    schemaVersion: data.schemaVersion,
    modelVersion: data.modelVersion || null,
  };
}

export async function listObservationsForUser(userId: string, sourceCategory?: ObservationSourceCategory) {
  if (sourceCategory === 'wearable') {
    const [canonicalSnapshot, legacySnapshot] = await Promise.all([
      getDocs(query(collection(db, 'observations'), where('userId', '==', userId), where('sourceCategory', '==', sourceCategory))),
      getDocs(collection(db, 'users', userId, 'wearable_observations')),
    ]);

    const canonical = canonicalSnapshot.docs.map((document) => ({ id: document.id, ...document.data() })) as ObservationRecord[];
    const legacy = legacySnapshot.docs.map((document) => mapLegacyWearableObservation(userId, document.id, document.data()));
    const seen = new Set(canonical.map((observation) => {
      const dateKey = observation.observedAt || '';
      const valueKey = observation.valueNumber ?? observation.valueString ?? '';
      return `${String(observation.display).trim().toLowerCase()}|${dateKey}|${valueKey}`;
    }));

    const merged = [
      ...canonical,
      ...legacy.filter((observation) => {
        const dateKey = observation.observedAt || '';
        const valueKey = observation.valueNumber ?? observation.valueString ?? '';
        const key = `${String(observation.display).trim().toLowerCase()}|${dateKey}|${valueKey}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    ];

    return sortObservationsNewestFirst(merged);
  }

  const constraints = [where('userId', '==', userId)];
  if (sourceCategory) {
    constraints.push(where('sourceCategory', '==', sourceCategory));
  }
  const snapshot = await getDocs(query(collection(db, 'observations'), ...constraints));
  return sortObservationsNewestFirst(
    snapshot.docs.map((document) => ({ id: document.id, ...document.data() })) as ObservationRecord[],
  );
}

export function buildLatestObservationMap(observations: ObservationRecord[]) {
  const latest = new Map<string, ObservationRecord>();

  for (const observation of sortObservationsNewestFirst(observations)) {
    const key = `${observation.sourceCategory}:${String(observation.code || observation.display).trim().toLowerCase()}`;
    if (!latest.has(key)) {
      latest.set(key, observation);
    }
  }

  return latest;
}

/**
 * Every member-facing lab read goes through here.
 *
 * Two things used to differ between the canonical and legacy paths, and callers
 * had to remember both: which collection wins, and whether the review gate was
 * applied. It wasn't — `filterVisibleToMember` was called only on the legacy
 * branch, so a result a clinician had explicitly held (`awaiting_review`) could
 * still reach the member through canonical `observations`.
 *
 * Note the emptiness check reads the RAW canonical list, before filtering. If a
 * member has canonical observations and every one of them is held, the answer is
 * an empty list — not a fallback to `lab_results`, which would resurrect exactly
 * the results the gate is holding. The legacy path is for members whose data
 * never reached `observations` at all.
 */
export async function listMemberVisibleLabResults(userId: string): Promise<LabResultRecord[]> {
  const canonical = await listObservationsForUser(userId, 'lab');
  if (canonical.length > 0) {
    return filterVisibleToMember(canonical.map(mapLabObservationToLabResultRecord));
  }

  const legacySnapshot = await getDocs(collection(db, 'users', userId, 'lab_results'));
  const legacy = legacySnapshot.docs.map(
    (document) => ({ id: document.id, ...document.data() }),
  ) as LabResultRecord[];
  return filterVisibleToMember(legacy);
}

export function mapLabObservationToLabResultRecord(observation: ObservationRecord): LabResultRecord {
  return {
    id: observation.id,
    biomarker: observation.display,
    value: observation.valueString || (observation.valueNumber != null ? String(observation.valueNumber) : ''),
    // Carry the fields the member biomarker display needs. Before this, the
    // canonical observations path silently dropped referenceRange + valueNumber,
    // so ranges/flags only rendered for docs read straight from lab_results.
    biomarkerKey: observation.code || null,
    // Censored bound + its symbol, or the member view re-reads "<7.00" as "7".
    valueComparator: observation.valueComparator || null,
    valueNumber: typeof observation.valueNumber === 'number' ? observation.valueNumber : null,
    referenceRange: observation.referenceRange || null,
    unit: observation.unit || null,
    observedAt: observation.observedAt || null,
    // Sample context, carried the same way referenceRange/valueNumber had to be:
    // this mapper is the single narrow point where the canonical path can lose
    // fields the member view needs, and it has lost them before.
    observedAtPrecision: observation.observedAtPrecision || null,
    collectedAt: observation.collectedAt || null,
    collectedAtPrecision: observation.collectedAtPrecision || null,
    fastingStatus: observation.fastingStatus ?? null,
    fastingRequested: observation.fastingRequested ?? null,
    derived: observation.derived || null,
    derivedEquation: observation.derivedEquation || null,
    interpretation: observation.interpretation || null,
    abnormalFlag: observation.abnormalFlag || null,
    sourceDocumentName: observation.sourceDocumentId || null,
    // Provenance of the DRAW rather than of the analyte: which panel it came
    // back on and who performed it. The results page names the draw as an event
    // ("Baseline (men) · 26 August · Inuvi"), and without these two fields that
    // header degrades to a bare date on the canonical path while rendering in
    // full on the legacy one — the exact split this mapper keeps losing.
    panel: observation.panel || null,
    sourceVendor: observation.sourceVendor || null,
    sourceType: observation.sourceType || null,
    sourceSystem: observation.sourceSystem || null,
    reviewState: observation.reviewState || null,
    createdAt: observation.createdAt,
    updatedAt: observation.updatedAt,
    schemaVersion: observation.schemaVersion,
    modelVersion: observation.modelVersion || null,
  };
}
